import React from 'react';
import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { adminIndexClients } from 'services/api/admin/clients';
import {
  AdminImport,
  adminImportsQueryKeys,
  adminIndexImports,
  adminUpdateImport,
  fetchImportFileUrl,
  ImportAction,
  ImportStatus,
} from 'services/api/admin/imports';
import type { AdminImportIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, string> = {
  [ImportStatus.PENDING]: colors.black25,
  [ImportStatus.RUNNING]: colors.primaryBlue,
  [ImportStatus.PAUSED]: colors.accentYellow,
  [ImportStatus.COMPLETED]: colors.accentGreen,
  [ImportStatus.ERRORED]: colors.accentRed,
  [ImportStatus.CANCELED]: colors.black50,
};

function ImportStatusBadge({ status }: { status: string }) {
  return (
    <StatusContainer>
      <StatusDot color={STATUS_COLORS[status] ?? colors.black25} />
      <span style={{ textTransform: 'capitalize' }}>{status}</span>
    </StatusContainer>
  );
}

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const statusOptions = Object.values(ImportStatus).map((s) => ({
  id: s,
  name: s.charAt(0).toUpperCase() + s.slice(1),
}));

const IMPORT_INCLUDES = ['child', 'stepCounts', 'client', 'file'] as const;

type ImportRow = AdminImport<AdminImportIndexFields<(typeof IMPORT_INCLUDES)[number]>>;

const config: AdminListPageConfig<ImportRow> = {
  title: 'Import History',
  queryKey: adminImportsQueryKeys.index(),
  includes: IMPORT_INCLUDES,
  fetchFn: (params, signal) => adminIndexImports(params, signal, [...IMPORT_INCLUDES]),
  fetchParams: { defaultSort: 'createdAt desc' },
  filters: [
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'importHistory', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc', hasImportConfiguration: true },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'dropdown',
      staticOptions: statusOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'createdAt',
      label: 'Date Range',
      type: 'date_range',
      defaultRange: 90,
    },
  ],

  columns: [
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => <strong>{row.client?.name ?? '—'}</strong>,
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => <ImportStatusBadge status={row.status} />,
    },
    {
      id: 'fileRowCount',
      header: 'Total Raw Rows',
      sortable: false,
      render: (row) => row.stepCounts?.fileRowCount?.toLocaleString() ?? '—',
    },
    {
      id: 'memberCount',
      header: 'Member Count',
      sortable: false,
      render: (row) => row.stepCounts?.memberCount?.toLocaleString() ?? '—',
    },
    {
      id: 'createdAt',
      header: 'Import Created On',
      render: (row) => (row.createdAt ? simpleDateAtTime(row.createdAt) : ''),
    },
  ],

  rowActions: [
    {
      label: 'View Status Details',
      onClick: (row, { navigate }) => navigate(`/admin/import-history/${row.id}/status-details`),
    },
    {
      label: 'Download File',
      visible: (row) => !row.fileExpired && !!row.file?.id,
      onClick: async (row, { toast }) => {
        if (!row.file?.id) return;
        try {
          const url = await fetchImportFileUrl(row.file.id);
          if (url) {
            window.open(url, '_blank');
          } else {
            toast('Unable to download file. Please try again.');
          }
        } catch {
          toast('Unable to download file. Please try again.');
        }
      },
    },
    {
      label: 'Resume Import',
      permission: 'adminImportedPatientEdit',
      visible: (row) => !!row.runnable,
      onClick: async (row, { refetch, toast }) => {
        try {
          await adminUpdateImport(row.id, { invokeAction: ImportAction.RUN });
          refetch();
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
    {
      label: 'Restart Import',
      permission: 'adminImportedPatientEdit',
      visible: (row) => !!row.restartable,
      onClick: async (row, { refetch, toast }) => {
        try {
          await adminUpdateImport(row.id, { invokeAction: ImportAction.RESTART });
          refetch();
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
    {
      label: 'Cancel Import',
      danger: true,
      permission: 'adminImportedPatientEdit',
      visible: (row) => !!row.cancellable,
      onClick: (row, { openModal }) => openModal('cancel', row),
    },
  ],

  modals: {
    cancel: {
      actionText: 'cancel',
      objType: 'import',
      onConfirm: async (row, { refetch, toast }) => {
        try {
          await adminUpdateImport(row.id, { invokeAction: ImportAction.CANCEL });
          refetch();
        } catch {
          toast('Something went wrong. Please try again.');
        }
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ImportHistory() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}
